import {ILoginResponse, IRefreshToken} from '../../types/Auth';
import createAuthAxiosInstance from '../createAuthAxiosInstance';

const postRegister = async (clientCode: string, username: string) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/register', {
    clientCode: clientCode,
    username: username,
    ageCheck: true,
  });
  return response.data;
};

const postFinishRegister = async (data: any) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/finish-register', {
    ...data,
  });
  return response.data;
};

const loginService = async (
  username: string,
  password: string,
): Promise<ILoginResponse> => {
  const instance = createAuthAxiosInstance();

  // Helper function to check if input is an email
  const isEmail = (input: string): boolean => {
    // More permissive email regex that allows common email formats
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const hasAtSymbol = input.includes('@');
    const hasDotAfterAt = input.split('@')[1]?.includes('.');
    return hasAtSymbol && hasDotAfterAt && emailRegex.test(input);
  };

  const emailDetected = isEmail(username);

  try {
    if (emailDetected) {
      try {
        // If input is an email, try network account login first
        const networkResponse = await instance.post('/v1/auth/login-network', {
          email: username,
          password,
        });

        console.log('[AuthService] Network login successful');
        return {
          ...networkResponse.data,
          user_type: 'network',
        };
      } catch (networkError) {
        const userResponse = await instance.post('/v1/auth/login', {
          username,
          password,
        });

        return {
          ...userResponse.data,
          user_type: 'client',
        };
      }
    } else {
      const userResponse = await instance.post('/v1/auth/login', {
        username,
        password,
      });

      return {
        ...userResponse.data,
        user_type: 'client',
      };
    }
  } catch (error: any) {
    throw error;
  }
};

const resendTokenPost = async (user: string, channel: 'whatsapp' | 'email' = 'whatsapp') => {
  // #region agent log
  fetch('http://127.0.0.1:7243/ingest/7dd560ae-ee35-4294-995f-b722aed46619',{
    method:'POST',
    headers:{
      'Content-Type':'application/json',
      'X-Debug-Session-Id':'12ff92',
    },
    body:JSON.stringify({
      sessionId:'12ff92',
      runId:'e2e',
      hypothesisId:'H8',
      location:'AuthService.ts:resendTokenPost',
      message:'Chamando /v1/auth/resend-token',
      data:{user,channel},
      timestamp:Date.now(),
    }),
  }).catch(()=>{});
  // #endregion

  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/resend-token', {
    user,
    channel,
  });
  return response.data;
};

const validateEmailTokenPost = async (code: string) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/validate-token', {
    token: code,
  });
  return response.data;
};

const recoverPasswordPost = async (user: string) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/reset-password', {
    user,
  });
  return response.data;
};

const validateResetTokenPost = async (user: string, token: string) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/validate-reset-token', {
    user,
    token,
  });
  return response.data;
};

const resendResetTokenPost = async (
  user: string,
  channel: 'whatsapp' | 'email' = 'whatsapp',
) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/resend-reset-token', {
    user,
    channel,
  });
  return response.data;
};

const confirmResetPasswordPost = async (data: {
  user: string;
  token: string;
  password: string;
  confirmPassword: string;
}) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/confirm-reset-password', data);
  return response.data;
};

const authCheckGet = async (token: string) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.get('/v1/auth/auth-check', {
    headers: {
      Authorization: token,
    },
  });
  return response.data;
};

const logoutPost = async () => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('/v1/auth/logout');
  return response.data;
};

const refreshToken = async (): Promise<IRefreshToken> => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('v1/auth/refreshToken');
  return response.data;
};

const getMyCompanies = async (token: string, clientCode: string) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.get('v1/client/myCompanies', {
    headers: {
      'client-code': clientCode,
      Authorization: token,
    },
  });
  return response.data;
};

const getMyCompaniesForUserType = async (token: string, userType: 'network' | 'client', networkAccountId?: number, clientCode?: string) => {
  const instance = createAuthAxiosInstance();

  if (userType === 'network' && networkAccountId) {
    const response = await instance.get(`v1/network-accounts/${networkAccountId}/clients`, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  } else if (userType === 'client' && clientCode) {
    return getMyCompanies(token, clientCode);
  }

  throw new Error('Invalid user type or missing parameters');
};

const getGroupRequests = async (token: string, clientCode: string) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.get('v1/group-requests', {
    headers: {
      Authorization: token,
    },
    params:{
      'client_code': clientCode,

    }
  });
  return response.data;
};

const postGroupRequests = async (token: string, groupName: string, cnpj: string, requestedsDocuments: Array<string>) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('v1/group-requests', {
    group_name: groupName,
    requesting_document: cnpj,
    requested_documents: requestedsDocuments,
  }, {
    headers: {
      Authorization: token,
    },
  });
  return response.data;
};

const changeAuthByCompany = async (
  token: string,
  clientCode: string,
  newClientCode: string,
) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post(
    'v1/auth/loginWithAnotherCompany',
    {
      clientCode: newClientCode,
    },
    {
      headers: {
        'client-code': clientCode,
        Authorization: token,
      },
    },
  );
  return response.data;
};

const postNotaFiscal = async (nfKey: string | string[]) => {
  const instance = createAuthAxiosInstance();
  const response = await instance.post('v1/client/my-order-nf', {
    nfkey: Array.isArray(nfKey) ? nfKey : [nfKey],
  });
  return response.data;
};

export default {
  postRegister,
  loginService,
  resendTokenPost,
  validateEmailTokenPost,
  postFinishRegister,
  recoverPasswordPost,
  validateResetTokenPost,
  resendResetTokenPost,
  confirmResetPasswordPost,
  authCheckGet,
  logoutPost,
  refreshToken,
  getMyCompanies,
  getMyCompaniesForUserType,
  getGroupRequests,
  changeAuthByCompany,
  postNotaFiscal,
  postGroupRequests
};
